import { useCallback, useMemo } from 'react';
import { useAssistantApi, useAssistantState } from '@assistant-ui/react';

import chatPasteGo from '@atom/components/common/svgIcons/chatPasteGo.svg';
import { Icon } from '@atom/mui';
import { AIChatToolIds } from '@atom/types/ai';

import { useTextFromMessage } from '../../hooks/useTextFromMessage';
import { useToolCalls } from '../../hooks/useToolCalls';
import { styles } from '../styles';
import { ChatAssistantMessageHookResponse } from '../types';

const ASK_APPROACH_MESSAGE =
  'Show me the approach you used to arrive at this answer.';

const RELEVANT_TOOLS = [
  AIChatToolIds.QUERY_CONVERSATIONAL_ANALYTICS,
  AIChatToolIds.QUERY_ENTITY_RAG,
] as string[];

export const useAskApproachAction = (): ChatAssistantMessageHookResponse => {
  const tools = useToolCalls();
  const api = useAssistantApi();
  const isLast = useAssistantState(state => state.message.isLast);
  const { messages } = useAssistantState(state => state.thread);

  const lastUserMessage = useMemo(() => {
    return [...messages].reverse().find(msg => msg.role === 'user');
  }, [messages]);

  const lastUserMessageText = useTextFromMessage(lastUserMessage);

  const hasRelevantToolCalls = useMemo(() => {
    return tools.some(({ toolName }) => RELEVANT_TOOLS.includes(toolName));
  }, [tools]);

  const available =
    hasRelevantToolCalls &&
    isLast &&
    lastUserMessageText !== ASK_APPROACH_MESSAGE;

  const handleClick = useCallback(() => {
    const runtime = api.thread();

    runtime.append({
      role: 'user',
      content: [
        {
          type: 'text',
          text: ASK_APPROACH_MESSAGE,
        },
      ],
    });
  }, [api]);

  return useMemo<ChatAssistantMessageHookResponse>(
    () => ({
      available,
      type: 'menu',
      ui: [
        {
          icon: (
            <Icon style={styles.menuItemIcon}>
              <img src={chatPasteGo} />
            </Icon>
          ),
          copy: 'Ask Approach',
          onClick: handleClick,
        },
      ],
    }),
    [available, handleClick],
  );
};
